import cs from "classnames";
import React from "react";
import { GoChevronDown, GoChevronUp } from "react-icons/go";

import {
	ColumnProps,
	RowCompareFn,
	RowData,
	RowProps,
	SortDirection
} from "src/common/components/DataTable/types";
import { useFilter } from "src/common/components/DataTable/useFilter";
import { useSort } from "src/common/components/DataTable/useSort";
import { Table as SimpleTable } from "src/common/components/Table";

import "./DataTable.css";

interface DataTableProps {
	columns: ColumnProps[];
	data: RowData[];
	query?: string;
	isSticky?: boolean;
	initialSortKey?: string;
	initialSortDirection?: SortDirection;
	noDataMessage?: React.ReactElement;
	renderActions?(data: RowData): React.ReactNode;
	onRowClick?(data: RowData): unknown;
	preCompareFn?: RowCompareFn;
}

export const DataTable: React.FunctionComponent<DataTableProps> = ({
	columns,
	data: tableData,
	query = "",
	isSticky = false,
	initialSortKey = columns[0].key,
	initialSortDirection = "asc",
	noDataMessage = null,
	renderActions,
	onRowClick,
	preCompareFn
}) => {
	const [hasChangedSortKey, setHasChangedSortKey] = React.useState(false);
	const [sortKey, setSortKey] = React.useState(initialSortKey);
	const [sortDirection, setSortDirection] =
		React.useState(initialSortDirection);

	const filteredTableData = useFilter(columns, tableData, query);
	const sortedTableData = useSort(
		columns,
		filteredTableData,
		sortKey,
		sortDirection,
		hasChangedSortKey ? undefined : preCompareFn
	);

	if (sortedTableData.length === 0) {
		return noDataMessage;
	}

	return (
		<SimpleTable
			className={cs("data-table", {
				"data-table--sticky": isSticky,
				"data-table--interactive": !!onRowClick
			})}
		>
			<thead>
				<tr>
					{columns.map(({ key, label }) => (
						<th
							key={key}
							className={
								sortKey === key
									? `sorted ${sortDirection}`
									: undefined
							}
							onClick={event => {
								event.preventDefault();
								event.stopPropagation();
								setHasChangedSortKey(true);
								if (sortKey === key) {
									setSortDirection(dir =>
										dir === "asc" ? "desc" : "asc"
									);
								} else {
									setSortKey(key);
								}
							}}
						>
							<div>
								{label}
								{sortKey === key && sortDirection === "asc" ? (
									<GoChevronUp />
								) : (
									<GoChevronDown />
								)}
							</div>
						</th>
					))}
					{renderActions && <th />}
				</tr>
			</thead>
			<tbody>
				{sortedTableData.map((rowData, i) => (
					<Row
						key={i}
						columns={columns}
						data={rowData}
						renderActions={renderActions}
						onRowClick={onRowClick}
					/>
				))}
			</tbody>
		</SimpleTable>
	);
};

const Row: React.FunctionComponent<RowProps> = ({
	columns,
	data,
	renderActions,
	onRowClick
}) => (
	<tr
		onClick={() => {
			if (onRowClick) {
				onRowClick(data);
			}
		}}
	>
		{columns.map(({ key, render }) => (
			<td key={key} data-fyrms={key}>
				{render === undefined
					? (data[key] as React.ReactNode)
					: render(data[key], data)}
			</td>
		))}
		{renderActions && (
			<td>
				<div className="actions">{renderActions(data)}</div>
			</td>
		)}
	</tr>
);
